import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../../redux/hooks';
import { useOnFarmActionBlocked } from '../../../hooks/useOnFarmActionBlocked';
import { AnimalCard, Loader, Modal } from '../../../components';
import { FilterIconSvg, PlusCircleSvg } from '../../../components/svgs/index';
import { FilterOption } from '../../../components/custom-input/FilterDropdown';
import OnFarmFilterControls from './OnFarmFilterControls';
import OnFarmAddAnimal from '../animal-management/OnFarmAddAnimal';
import { filtersType } from '../../inventory/Inventory';
import { Animal } from '../../../types/interfaces/animal.interfaces';
import { Breed, Species, SpecimenType } from '../../../types/interfaces';
import { InventorySource } from '../../../types/enums';
import { getSpecimenTypes } from '../../../api/specimensTypesApi';
import { getSpecies } from '../../../api/speciesApi';
import { getBreedsOfSpecies } from '../../../api/breedsApi';
import { showToast } from '../../../services/toast.service';
import { getOnFarmAccountAnimalsOdata } from '../../../api/onFarmAnimalsApi';
import { getOnFarmTotalUnits, toPagedOnFarmAnimals } from '../../../services/onFarmInventory.service';
import { StandardParamsToODataParams } from '../../../types/interfaces/apiParams.interfaces';
import {
  ANIMAL_CONSTANTS,
  BUTTON_CONSTANTS,
  LABEL_CONSTANTS,
  PAGE_HEADER_CONSTANTS,
} from '../../../constants/common';
import { PromiseError, toastMessages } from '../../../constants/errorMessages';

const defaultFilters: filtersType = {
  search: '',
  type: { name: '', value: null },
  species: { name: '', value: null },
  breed: { name: '', value: null },
  sort: { name: '', value: null },
};

type OnFarmInventorySnapshot = {
  accountId?: number;
  animals: Animal[];
  filters: filtersType;
  pagingOffset: number;
  pagingMax: number;
  animalCount?: number;
  scrollY: number;
};

let onFarmInventorySnapshot: OnFarmInventorySnapshot | null = null;

const buildOnFarmAnimalODataFilter = (filters: filtersType): string | undefined => {
  const clauses: string[] = [];
  const search = filters.search.trim();

  if (search) {
    const escaped = search.replace(/'/g, "''");
    clauses.push(`(contains(name,'${escaped}') or contains(code,'${escaped}'))`);
  }

  if (filters.breed.value) {
    clauses.push(`breedId eq ${filters.breed.value}`);
  }

  if (filters.species.value) {
    clauses.push(`breed/speciesId eq ${filters.species.value}`);
  }

  if (filters.type.value) {
    clauses.push(`specimens/any(s: s/specimenTypeId eq ${filters.type.value})`);
  }

  return clauses.length ? clauses.join(' and ') : undefined;
};

const OnFarmInventory: FC = (): JSX.Element => {
  const pagingSize = 8;
  const { accountId } = useAppSelector(state => state.activeAccount);
  const isOnFarmLocked = useOnFarmActionBlocked();
  const snapshot = accountId != null && onFarmInventorySnapshot?.accountId === accountId ? onFarmInventorySnapshot : null;
  const skipNextFetchRef = useRef(!!snapshot && (snapshot.animals.length > 0 || snapshot.pagingOffset > 0));
  const restoredScrollYRef = useRef(snapshot?.scrollY ?? 0);
  const isLoadingRef = useRef(false);
  const stateRef = useRef({
    accountId,
    animals: snapshot?.animals ?? [],
    filters: snapshot?.filters ?? defaultFilters,
    pagingOffset: snapshot?.pagingOffset ?? 0,
    pagingMax: snapshot?.pagingMax ?? 0,
    animalCount: snapshot?.animalCount,
  });

  const [animals, setAnimals] = useState<Animal[]>(snapshot?.animals ?? []);
  const [totalUnits, setTotalUnits] = useState<number>();
  const [animalCount, setAnimalCount] = useState<number | undefined>(snapshot?.animalCount);
  const [pagingOffset, setPagingOffset] = useState<number>(snapshot?.pagingOffset ?? 0);
  const [pagingMax, setPagingMax] = useState<number>(snapshot?.pagingMax ?? 0);
  const [typeOptions, setTypeOptions] = useState<FilterOption<number | null>[]>([]);
  const [speciesOptions, setSpeciesOptions] = useState<FilterOption<number | null>[]>([]);
  const [breedOptions, setBreedOptions] = useState<FilterOption<number | null>[]>([]);
  const [isOpenFilterInventoryModal, setIsOpenFilterInventoryModal] = useState<boolean>(false);
  const [addAnimalModal, setAddAnimalModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [listRefresh, setListRefresh] = useState(0);
  const [filters, setFilters] = useState<filtersType>(snapshot?.filters ?? defaultFilters);

  stateRef.current = {
    accountId,
    animals,
    filters,
    pagingOffset,
    pagingMax,
    animalCount,
  };

  const sortOptions: FilterOption<string | null>[] = [
    { name: 'All', value: null },
    { name: 'Name (A-Z)', value: 'Name' },
    { name: 'Name (Z-A)', value: '^Name' },
    { name: 'Youngest', value: '^birthDate' },
    { name: 'Eldest', value: 'birthDate' },
  ];

  const handleGetSpecimenTypes = useCallback(async () => {
    try {
      const response = await getSpecimenTypes();
      const newType = response.data as SpecimenType[];
      setTypeOptions([
        { name: 'All', value: null },
        ...newType.map((specimenType: SpecimenType) => {
          return { name: specimenType.name!, value: specimenType.specimenTypeId };
        }),
      ]);
    } catch (error: any) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  }, []);

  const handleGetAllSpecies = useCallback(async () => {
    try {
      const response = await getSpecies();
      const newSpecies = response.data as Species[];
      setSpeciesOptions([
        { name: 'All', value: null },
        ...newSpecies.map((species: any) => {
          return { name: species.name, value: species.speciesId };
        }),
      ]);
    } catch (error: any) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  }, []);

  const handleGetBreedOptions = useCallback(async (speciesId: number) => {
    if (speciesId === null) {
      setBreedOptions([{ name: 'All', value: null }]);
    } else
      try {
        const response = await getBreedsOfSpecies(speciesId);
        const newBreeds = response.data as Breed[];
        setBreedOptions([
          { name: 'All', value: undefined },
          ...newBreeds.map((breeds: any) => {
            return { name: breeds.name, value: breeds.breedId };
          }),
        ]);
      } catch (error: any) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
  }, []);

  useEffect(() => {
    handleGetSpecimenTypes();
  }, [handleGetSpecimenTypes]);

  useEffect(() => {
    handleGetAllSpecies();
  }, [handleGetAllSpecies]);

  useEffect(() => {
    if (filters.species.value) {
      handleGetBreedOptions(filters.species.value);
    }
  }, [filters.species.value, handleGetBreedOptions]);

  useEffect(() => {
    const loadInventorySummary = async () => {
      try {
        const { data: total } = await getOnFarmTotalUnits(accountId);
        setTotalUnits(total);
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    };
    loadInventorySummary();
  }, [accountId, listRefresh]);

  const fetchItems = useCallback(
    async (offset = 0, currentAnimals: Animal[] = []) => {
      if (!accountId) {
        setAnimals([]);
        setAnimalCount(0);
        setPagingMax(0);
        setPagingOffset(0);
        return;
      }

      if (isLoadingRef.current && offset !== 0) return;
      isLoadingRef.current = true;
      setIsLoading(true);

      try {
        const { data } = await getOnFarmAccountAnimalsOdata(
          StandardParamsToODataParams({
            include: 'Breed.Species, Attachments',
            limit: pagingSize + 1,
            offset,
            filter: buildOnFarmAnimalODataFilter(filters),
            sort: filters.sort.value ?? undefined,
          }),
        );
        const pagedAnimals = toPagedOnFarmAnimals(data);

        const hasMore = pagedAnimals.result.length > pagingSize;
        const newAnimals = hasMore ? pagedAnimals.result.slice(0, pagingSize) : pagedAnimals.result;
        const loadedCount = offset + newAnimals.length;

        setAnimals(offset === 0 ? newAnimals : [...currentAnimals, ...newAnimals]);
        setPagingOffset(loadedCount);
        setPagingMax(hasMore ? loadedCount + 1 : loadedCount);
        setAnimalCount(loadedCount);
      } catch (error: any) {
        const errorCode = error.code;
        if (errorCode === PromiseError.NETWORK) {
          showToast.error(toastMessages.NETWORK);
        } else if (errorCode === PromiseError.BAD_REQUEST) {
          showToast.error(PromiseError.BAD_REQUEST);
        } else {
          showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        }
      } finally {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    },
    [filters, accountId],
  );

  useEffect(() => {
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      const scrollY = restoredScrollYRef.current;
      const restoreScroll = () => window.scrollTo(0, scrollY);
      restoreScroll();
      const timeoutId = window.setTimeout(restoreScroll, 50);
      return () => window.clearTimeout(timeoutId);
    }

    fetchItems(0, []);
  }, [fetchItems, listRefresh]);

  useEffect(() => {
    return () => {
      const current = stateRef.current;
      if (!current.accountId) return;

      onFarmInventorySnapshot = {
        ...current,
        scrollY: window.scrollY,
      };
    };
  }, []);

  const handleFilterInventoryModalClose = () => {
    setIsOpenFilterInventoryModal(false);
  };

  const handleFilterInventoryModalSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setFilters(filters);
    setIsOpenFilterInventoryModal(false);
  };

  const handleAddAnimalClose = (animalId?: number) => {
    setAddAnimalModal(false);
    if (animalId) {
      onFarmInventorySnapshot = null;
      setListRefresh(prev => prev + 1);
    }
  };

  return (
    <div>
      <div className="on-farm-inventory">
        <div className="inventory-overview">
          <h1>{PAGE_HEADER_CONSTANTS.ON_FARM_INVENTORY}</h1>
          <div className="overview-stats">
            <p>
              <span>{animalCount ?? '--'}</span>
              {ANIMAL_CONSTANTS.ANIMALS}
            </p>
            <p>
              <span>{totalUnits ?? '--'}</span>
              {LABEL_CONSTANTS.TOTAL_UNITS}
            </p>
          </div>
          <button
            type="button"
            className="button outlined add-animal-button"
            onClick={() => setAddAnimalModal(true)}
            disabled={isOnFarmLocked}
            aria-label={ANIMAL_CONSTANTS.ADD_NEW_ANIMAL}>
            <PlusCircleSvg />
            <span className="add-animal-label">{ANIMAL_CONSTANTS.ADD_NEW_ANIMAL}</span>
          </button>
        </div>
        <div className="inventory-search search-dropdown show-icon">
          <button className="mobile-view" onClick={() => setIsOpenFilterInventoryModal(true)}>
            <FilterIconSvg />
          </button>
          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder="Search"
              value={filters.search}
              onChange={e => setFilters({ ...filters, search: e.target.value })}
            />
          </div>
        </div>
        <div className="filters desktop-view">
          <OnFarmFilterControls
            filters={filters}
            setFilters={setFilters}
            typeOptions={typeOptions}
            speciesOptions={speciesOptions}
            breedOptions={breedOptions}
            sortOptions={sortOptions}
            handleGetBreedOptions={handleGetBreedOptions}
          />
        </div>
        <div className="gallery">
          <div className="gallery-heading">
            <h2>{LABEL_CONSTANTS.VIEWING}</h2>
            <p>
              {pagingOffset} of {pagingMax}
            </p>
          </div>
          {!isLoading && animals.length === 0 ? (
            <div className="result-item">{LABEL_CONSTANTS.NO_INVENTORY}</div>
          ) : (
            <div className="items">
              {animals.map((animal: Animal) => (
                <div key={`on-farm-item-${animal.animalId ?? animal.code}`}>
                  <AnimalCard animal={animal} accountId={accountId} inventorySource={InventorySource.ON_FARM} />
                </div>
              ))}
            </div>
          )}
          {isLoading && <Loader loaderSize="medium" appLoader />}
          {pagingOffset < pagingMax && (
            <button type="button" className="button blue" onClick={() => fetchItems(pagingOffset, animals)}>
              {BUTTON_CONSTANTS.VIEW_MORE}
            </button>
          )}
        </div>
      </div>
      {isOpenFilterInventoryModal && (
        <Modal isOpen={isOpenFilterInventoryModal} onClose={handleFilterInventoryModalClose} ignoreBackdrop>
          <div className="filter-inventory">
            <h4>{LABEL_CONSTANTS.FILTER}</h4>
            <hr />
            <form onSubmit={handleFilterInventoryModalSubmit}>
              <OnFarmFilterControls
                filters={filters}
                setFilters={setFilters}
                typeOptions={typeOptions}
                speciesOptions={speciesOptions}
                breedOptions={breedOptions}
                sortOptions={sortOptions}
                handleGetBreedOptions={handleGetBreedOptions}
                isModal
                onCancel={handleFilterInventoryModalClose}
              />
            </form>
          </div>
        </Modal>
      )}
      <Modal isOpen={addAnimalModal} onClose={() => setAddAnimalModal(false)} width={1014} ignoreBackdrop>
        <OnFarmAddAnimal onCancel={handleAddAnimalClose} />
      </Modal>
    </div>
  );
};

export default OnFarmInventory;
